// PTESPL Proposal Template Library
// 8 standard templates aligned with the PT Ventures LLP proposal format.
// Each template returns: cover meta, intro, applicability rows,
// scope sections (table rows), fee items, payment milestones, additional T&Cs, conclusion.

export const PROPOSAL_TYPES = [
  "Fresh_EC_CTE", "EC_Amendment", "EC_Expansion", "EC_Corrigendum",
  "CTO", "CRZ", "MEP", "Compliance_Monitoring",
];

export const PROPOSAL_TYPE_META = {
  Fresh_EC_CTE: { label: "Fresh EC + CTE", title: "Obtaining Environmental Clearance & Consent to Establish", service_type: "Fresh EC + CTE" },
  EC_Amendment: { label: "EC Amendment", title: "Amendment of Environmental Clearance", service_type: "EC Amendment" },
  EC_Expansion: { label: "EC Expansion", title: "Expansion Environmental Clearance", service_type: "EC Expansion" },
  EC_Corrigendum: { label: "EC Corrigendum", title: "Corrigendum to Environmental Clearance", service_type: "EC Corrigendum" },
  CTO: { label: "CTO", title: "Obtaining Consent to Operate (CTO)", service_type: "CTO" },
  CRZ: { label: "CRZ Clearance", title: "Coastal Regulation Zone (CRZ) Clearance", service_type: "CRZ Clearance" },
  MEP: { label: "MEP Approval", title: "MEP Design & Documentation for MoEF Approval", service_type: "MEP" },
  Compliance_Monitoring: { label: "Compliance Monitoring", title: "Post-EC Compliance Monitoring", service_type: "ECMP Retainer" },
};

// Helper: standard additional T&Cs aligned with PT Ventures sample
const STD_TERMS = [
  "All necessary project information and supporting documents shall be provided by the client.",
  "Statutory/government fees such as processing charges or BG value to be paid by the client.",
  "Any additional studies or permissions asked by SEAC/SEIAA will be charged extra.",
  "Drawings like MEP/CRZ/NBWL or any third-party NOCs are not part of this scope.",
  "Work beyond the stated scope shall be chargeable on actual basis.",
  "All travel and logistics expenses for outstation tasks will be reimbursed separately.",
  "Any Additional expenses will be extra as actual.",
  "Architectural & calculation/contour plan to be provided by client.",
  "Any delay due to violation, or any action taken by the authorities against the developer due to violation will not be a responsibility of the consultant (PTESPL). Any penalty imposed due to violation will not be a responsibility of the Consultant (PTESPL).",
];

const STD_CONCLUSION = (clientName) =>
  `We are committed to ensuring that ${clientName || "the Client"} meets all environmental regulatory obligations in a timely and efficient manner. PT Ventures LLP brings to the table industry expertise, regulatory know-how, and structured coordination capabilities that will ensure full support through every stage of your environmental compliance journey.\n\nWe look forward to a fruitful association.`;

// EC scope rows (from VIVA School proposal — used by Fresh_EC_CTE and as base for others)
const EC_SCOPE_ROW = {
  sr_no: 1,
  description: "Stakeholder Communication for Facilitation of Environmental Clearance",
  scope_items: [
    "Coordination with SEIAA, SEAC, MoEF & CC, MPCB, and other consultants",
    "Documentation, application follow-up, and presentation support",
    "Assistance in obtaining related reports",
    "Make site visit to identify the viability of project as per environment concern",
    "Coordinate with architect & geotechnical consultant and collect all information",
    "Prepare all requisite documents like Form I, IA, Conceptual Plan",
    "Prepare the Environmental Management Plan (EMP) — mitigation measures item-wise across construction, operation and life cycle",
    "Prepare site management plan during construction phase",
    "Prepare water regime/water balance diagram based on proposed utility & infrastructure",
    "Prepare a scheme for sewage treatment providing primary, secondary & tertiary treatment and recycling/reuse of treated water",
    "Prepare water management plan considering recycle/reuse of treated water in flushing, gardening & other non-potable uses",
    "Prepare a solid waste management plan & reuse/recycle & disposal scheme",
    "Attend personal hearing/technical presentation before State Level Expert Appraisal Committee (SEAC)",
    "Prepare Traffic Impact Assessment report, Disaster Management Plan, Light & Ventilation Study, Wind Study and shadow analysis required for project",
    "Liaise & follow up with SEAC & all concerned authorities for processing the proposal",
    "Submit additional information to expert committee, as per requirement",
    "Obtain Environmental Clearance from SEIAA",
    "Assistance in obtaining Public Notice",
  ],
};

const CTE_SCOPE_ROW = {
  sr_no: 2,
  description: "Stakeholder Communication for Consent to Establish (CTE)",
  scope_items: [
    "Attending MPCB Visits & MPCB Meetings",
    "Filling up application forms & Demand Notes (Water, Air & Hazardous Wastes) for MPCB Consent to Establish",
    "Collection of documentary data and preparing attachments/annexures for application",
    "Keeping follow up with concerned department for earliest processing",
    "Liaisoning with Maharashtra Pollution Control Board for obtaining Consent to Establish",
    "Obtaining Inspection Report from MPCB for SEAC consideration",
  ],
};

export const PROPOSAL_TEMPLATES = {
  Fresh_EC_CTE: {
    ...PROPOSAL_TYPE_META.Fresh_EC_CTE,
    intro: (c) => `PT Ventures LLP is pleased to present this proposal to provide Environmental Compliance services for the project ${c.project_name || c.client_name}. The scope includes end-to-end coordination for Environmental Clearance (EC) & Integrated Consent to Establish for your project.`,
    applicability_note: "As per EIA notification 2006 up to date, Environmental Clearance is mandatory for new projects or activities, or on the expansion or modernization of existing projects or activities based on their potential environmental impacts as indicated in the Schedule to the notification, being undertaken in any part of India. Therefore, you must require environmental clearance under 8(a) category of EIA Notification:",
    applicability_rows: [
      { sno: "8(a)", particular: "Building & construction projects", applicability: "BUA* ≥ 20,000 sq.m & < 1,50,000 sq.m", category: "B2", authority: "SEIAA" },
    ],
    applicability_footnote: "* BUA includes total construction area like balcony, passage, stilt, staircase, basement etc.",
    scope_sections: [
      { title: "A. Environmental Approval Management", rows: [EC_SCOPE_ROW, CTE_SCOPE_ROW] },
    ],
    fee_items: [
      { description: "Consultancy Fees for obtaining Environmental Clearance for Building & Construction project — BUA ≥ 20,000 sq.m & < 1,50,000 sq.m", amount: 900000 },
      { description: "Against Traffic Impact Assessment, Disaster Management Plan, Light & Ventilation Study, Carbon Foot Print Study, Wind Study and shadow analysis", amount: 0 },
      { description: "Against Site Inspection Report from MPCB for Integrated Consent to Establish", amount: 0 },
    ],
    payment_milestones: [
      "25% advance along with work order",
      "25% against presentation to SEAC",
      "50% against receipt of EC letter",
    ],
    additional_terms: STD_TERMS,
    conclusion: STD_CONCLUSION,
  },

  EC_Amendment: {
    ...PROPOSAL_TYPE_META.EC_Amendment,
    intro: (c) => `PT Ventures LLP is pleased to present this proposal to obtain an Amendment to the existing Environmental Clearance for ${c.project_name || c.client_name}. The scope covers documentation, justification report and liaison with SEIAA for amendment grant.`,
    applicability_note: "Amendment to Environmental Clearance is applicable when minor changes are proposed to the approved project that do not require fresh EC.",
    applicability_rows: [
      { sno: "—", particular: "Amendment to existing EC", applicability: "Minor changes within sanctioned BUA / scope", category: "Same as original", authority: "SEIAA" },
    ],
    applicability_footnote: "",
    scope_sections: [
      { title: "A. EC Amendment Management", rows: [{
          sr_no: 1,
          description: "Stakeholder Communication for Amendment of Environmental Clearance",
          scope_items: [
            "Review of existing EC, sanctioned plan and proposed changes",
            "Preparation of amendment justification note and comparison statement",
            "Coordination with SEIAA / SEAC / MoEF as applicable",
            "Drafting of revised Form-I / Form-IA where required",
            "Submission and follow-up of amendment application on Parivesh portal",
            "Attending SEAC / SEIAA presentations for amendment",
            "Obtaining Amended EC letter from SEIAA",
          ],
        }] },
    ],
    fee_items: [
      { description: "Consultancy Fees for EC Amendment", amount: 300000 },
    ],
    payment_milestones: [
      "50% advance along with work order",
      "50% against receipt of Amended EC letter",
    ],
    additional_terms: STD_TERMS,
    conclusion: STD_CONCLUSION,
  },

  EC_Expansion: {
    ...PROPOSAL_TYPE_META.EC_Expansion,
    intro: (c) => `PT Ventures LLP is pleased to present this proposal for Expansion of Environmental Clearance for ${c.project_name || c.client_name}. The scope covers preparation of expansion documents, EMP for the expanded portion and liaison with SEIAA until grant of Expansion EC.`,
    applicability_note: "Expansion EC is required when the proposed BUA / scope exceeds the originally sanctioned limits.",
    applicability_rows: [
      { sno: "8(a)", particular: "Expansion of Building & Construction project", applicability: "Cumulative BUA after expansion crosses category threshold", category: "B2/B1", authority: "SEIAA" },
    ],
    applicability_footnote: "",
    scope_sections: [
      { title: "A. EC Expansion Management", rows: [{
          ...EC_SCOPE_ROW,
          description: "Stakeholder Communication for Expansion of Environmental Clearance",
          scope_items: [
            "Review of existing EC, sanctioned plan and expanded scope",
            ...EC_SCOPE_ROW.scope_items.slice(1),
          ],
        }] },
    ],
    fee_items: [
      { description: "Consultancy Fees for Expansion EC", amount: 750000 },
    ],
    payment_milestones: [
      "25% advance along with work order",
      "25% against presentation to SEAC",
      "50% against receipt of Expansion EC letter",
    ],
    additional_terms: STD_TERMS,
    conclusion: STD_CONCLUSION,
  },

  EC_Corrigendum: {
    ...PROPOSAL_TYPE_META.EC_Corrigendum,
    intro: (c) => `PT Ventures LLP is pleased to present this proposal for issuance of Corrigendum to the existing Environmental Clearance for ${c.project_name || c.client_name}. The scope covers preparation of corrigendum request and follow-up until issuance.`,
    applicability_note: "Corrigendum to EC is required for correction of factual errors or change of name / address etc. in the existing EC letter.",
    applicability_rows: [
      { sno: "—", particular: "Corrigendum to existing EC", applicability: "Correction in name, area, or factual data", category: "Same as original", authority: "SEIAA" },
    ],
    applicability_footnote: "",
    scope_sections: [
      { title: "A. EC Corrigendum Management", rows: [{
          sr_no: 1,
          description: "Stakeholder Communication for EC Corrigendum",
          scope_items: [
            "Review of existing EC and identification of corrigendum points",
            "Preparation of corrigendum request with supporting documents",
            "Submission and follow-up with SEIAA",
            "Obtaining the Corrigendum letter",
          ],
        }] },
    ],
    fee_items: [
      { description: "Consultancy Fees for EC Corrigendum", amount: 150000 },
    ],
    payment_milestones: [
      "50% advance along with work order",
      "50% against receipt of Corrigendum letter",
    ],
    additional_terms: STD_TERMS,
    conclusion: STD_CONCLUSION,
  },

  CTO: {
    ...PROPOSAL_TYPE_META.CTO,
    intro: (c) => `PT Ventures LLP is pleased to present this proposal for obtaining the Consent to Operate (CTO) under the Water, Air & Hazardous Waste Acts from MPCB for the project ${c.project_name || c.client_name}.`,
    applicability_note: "Consent to Operate is required from MPCB prior to commencement of operations / occupancy of the project.",
    applicability_rows: [
      { sno: "—", particular: "Building / Industry post-construction", applicability: "Before commencement of operations / occupancy", category: "—", authority: "MPCB" },
    ],
    applicability_footnote: "",
    scope_sections: [
      { title: "A. MPCB Consent to Operate", rows: [{
          sr_no: 1,
          description: "Stakeholder Communication for Consent to Operate (CTO)",
          scope_items: [
            "Filling up application forms & demand notes (Water, Air & Hazardous Wastes) for MPCB Consent to Operate",
            "Attending MPCB visits and meetings",
            "Collection of documentary data and preparing attachments/annexures",
            "Liaisoning with MPCB sub-regional, regional & head office",
            "Follow-up for inspection and processing",
            "Obtaining Consent to Operate letter from MPCB",
          ],
        }] },
    ],
    fee_items: [
      { description: "Consultancy Fees for MPCB Consent to Operate", amount: 350000 },
    ],
    payment_milestones: [
      "25% advance along with work order",
      "25% against application submission",
      "50% against receipt of CTO",
    ],
    additional_terms: STD_TERMS,
    conclusion: STD_CONCLUSION,
  },

  CRZ: {
    ...PROPOSAL_TYPE_META.CRZ,
    intro: (c) => `PT Ventures LLP is pleased to present this proposal for obtaining Coastal Regulation Zone (CRZ) Clearance for the project ${c.project_name || c.client_name} located in the notified CRZ area.`,
    applicability_note: "As per CRZ Notification 2019, projects falling within the Coastal Regulation Zone require clearance from MCZMA / SCZMA / MoEF & CC depending on the category.",
    applicability_rows: [
      { sno: "—", particular: "CRZ Project", applicability: "Project located within 500m HTL", category: "CRZ-II / III / IV", authority: "MCZMA / MoEF & CC" },
    ],
    applicability_footnote: "",
    scope_sections: [
      { title: "A. CRZ Clearance Management", rows: [{
          sr_no: 1,
          description: "Stakeholder Communication for CRZ Clearance",
          scope_items: [
            "Site visit and demarcation coordination with authorised agency (NIO / NCSCM / NCESS)",
            "Preparation of CRZ map and project applicability note",
            "Drafting of Form-I, project report, EMP and other CRZ documents",
            "Submission and follow-up with MCZMA / SCZMA / MoEF",
            "Attending MCZMA / SCZMA presentations",
            "Obtaining CRZ Clearance / recommendation letter",
          ],
        }] },
    ],
    fee_items: [
      { description: "Consultancy Fees for CRZ Clearance", amount: 600000 },
      { description: "CRZ demarcation by authorised agency (at actuals)", amount: 0 },
    ],
    payment_milestones: [
      "25% advance along with work order",
      "25% against MCZMA presentation",
      "50% against receipt of CRZ clearance",
    ],
    additional_terms: STD_TERMS,
    conclusion: STD_CONCLUSION,
  },

  MEP: {
    ...PROPOSAL_TYPE_META.MEP,
    intro: (c) => `PT Ventures LLP is pleased to present this proposal for MEP design and documentation support for the project ${c.project_name || c.client_name} as required for environmental compliance and MoEF submission.`,
    applicability_note: "MEP design layouts, calculations and reports are required as part of EC documentation and SEAC presentation.",
    applicability_rows: [
      { sno: "—", particular: "MEP for Environmental Approvals", applicability: "Plumbing, Firefighting, Electrical, HVAC, STP, OWC", category: "—", authority: "MoEF / SEIAA" },
    ],
    applicability_footnote: "",
    scope_sections: [
      { title: "A. MEP Design & Documentation", rows: [{
          sr_no: 1,
          description: "Design — Preliminary layouts and calculations",
          scope_items: [
            "Plumbing, Firefighting, Electrical, HVAC/Ventilation layouts",
            "Load calculations, transformer/DG sizing, energy savings, solar design",
            "Water supply, sewerage, storm water, RWH design",
            "OWC and STP sizing & flow diagram",
          ],
        }, {
          sr_no: 2,
          description: "Documentation & Coordination",
          scope_items: [
            "MoEF documentation preparation and meeting attendance",
            "Coordination with Architect / MoEF consultant",
            "Coordination meetings and revisions",
          ],
        }],
      },
    ],
    fee_items: [
      { description: "Consultancy Fees for MEP Design & Documentation", amount: 450000 },
    ],
    payment_milestones: [
      "50% advance along with work order",
      "50% against submission of calculations & coordinated layout",
    ],
    additional_terms: STD_TERMS,
    conclusion: STD_CONCLUSION,
  },

  Compliance_Monitoring: {
    ...PROPOSAL_TYPE_META.Compliance_Monitoring,
    intro: (c) => `PT Ventures LLP is pleased to present this proposal for Post-EC Compliance Monitoring (ECMP Retainer) services for ${c.project_name || c.client_name} from construction stage until Occupancy Certificate / society handover.`,
    applicability_note: "Post-EC compliance monitoring is mandated by SEIAA / MoEF & CC and requires six-monthly compliance reports and BG returns.",
    applicability_rows: [
      { sno: "—", particular: "Post-EC Compliance", applicability: "Continuous through construction & operation", category: "Same as EC", authority: "SEIAA / MoEF & CC / MPCB" },
    ],
    applicability_footnote: "",
    scope_sections: [
      { title: "A. Environmental Compliance Management", rows: [{
          sr_no: 1,
          description: "Compliance Reporting & Monitoring",
          scope_items: [
            "Preparation & submission of Six-Monthly Compliance Reports",
            "Form-V Compliance Reports",
            "Public Notices & BG documentation coordination",
            "CTO, Six-monthly, Form V follow-up with MPCB / SEIAA",
            "Society handover documentation",
          ],
        }, {
          sr_no: 2,
          description: "Procurement & Vendor Support",
          scope_items: [
            "BOQ preparation & vendor procurement support for STP / OWC / AQMS / Anti-Smog Gun / RWH / Solar",
            "Vendor evaluation and recommendation",
          ],
        }],
      },
    ],
    fee_items: [
      { description: "Annual Retainer — invoiced half-yearly (duration rounded to nearest half-year)", amount: 600000 },
    ],
    payment_milestones: [
      "Half-yearly in advance",
    ],
    additional_terms: STD_TERMS,
    conclusion: STD_CONCLUSION,
  },
};
